/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import {
  DISTRIBUTION_LIST_BASE_URL,
  GENERIC_DISTRIBUTION_LIST_BASE_URL,
} from "Constants/SagaConstants";
import {
  IAddMicrosoftMemberToDLRequest,
  IFetchMicrosoftMembersForDLRequest,
  IRemoveMicrosoftMemberFromDLRequest,
} from "Dataflows/DistributionLists/DistributionListsAction";
import { IDistributionListFetchRequest } from "Interfaces/DistributionLists/IDistributionListFetchRequest";
import { IDistributionListReviewRequest } from "Interfaces/DistributionLists/IDistributionListReviewRequest";
import { IDistributionListsDataResult } from "Interfaces/DistributionLists/IDistributionListsDataResult";
import { IDistributionListsRequest } from "Interfaces/DistributionLists/IDistributionListsRequest";

import { IApiEmptyResult, IApiResult2 } from "../Interfaces/IApiResult";
import { get, post, put, remove, IHttpResponse } from "./ApiUtils";

/**
 * Search all distribution list.
 */
export async function searchDistributionLists(
  searchRequest: IDistributionListsRequest,
  tenant: string
): Promise<IHttpResponse<IApiResult2<IDistributionListsDataResult>>> {
  const url =
    DISTRIBUTION_LIST_BASE_URL.replace("{tenant}", tenant) +
    `requests/${searchRequest.pageIndex}/${searchRequest.pageSize}`;
  return await get<IApiResult2<IDistributionListsDataResult>>(url, true);
}

/**
 * Approve the dl join request.
 */
export async function approveOrDeclineDlJoinRequest(
  request: IDistributionListReviewRequest
): Promise<IHttpResponse<IApiEmptyResult>> {
  const baseUrl = DISTRIBUTION_LIST_BASE_URL.replace("{tenant}", request.tenant);
  const url = `${baseUrl}request`;
  return await put<IApiResult2<IDistributionListsDataResult>>(url, request, true);
}

/**
 * Fetch all DLs for a given tenant.
 */
export async function fetchDistributionListsByTenant(request: IDistributionListFetchRequest) {
  const url = `${GENERIC_DISTRIBUTION_LIST_BASE_URL}search`;
  return await post<IApiResult2<IDistributionListsDataResult>>(url, request, true);
}

export async function fetchMicrosoftMembersForDL(request: IFetchMicrosoftMembersForDLRequest) {
  const email = request.dlAlias.split("@")[0];
  request.dlAlias = email;
  const dlAlias = email.replace("C++", "CPLUSPLUS"); // Workaround: Double escape issue
  const url =
    DISTRIBUTION_LIST_BASE_URL.replace("{tenant}", request.tenant) + `${dlAlias}/MicrosoftMembers`;
  return await get<IApiResult2<IDistributionListsDataResult>>(url, true);
}

export async function addMicrosoftMemberToDL(request: IAddMicrosoftMemberToDLRequest) {
  const email = request.dlAlias.split("@")[0];
  request.dlAlias = email;
  const dlAlias = email.replace("C++", "CPLUSPLUS"); // Workaround: Double escape issue
  const url = DISTRIBUTION_LIST_BASE_URL.replace("{tenant}", request.tenant) + `${dlAlias}`;
  return await post<IApiResult2<IDistributionListsDataResult>>(url, request, true);
}

export async function removeMicrosoftMemberFromDL(request: IRemoveMicrosoftMemberFromDLRequest) {
  const email = request.dlAlias.split("@")[0];
  request.dlAlias = email;
  const dlAlias = email.replace("C++", "CPLUSPLUS"); // Workaround: Double escape issue
  const url =
    DISTRIBUTION_LIST_BASE_URL.replace("{tenant}", request.tenant) +
    `${dlAlias}/member/${request.memberEmail}`;
  return await remove<IApiResult2<IApiEmptyResult>>(url, request, true);
}
